"use client";

import { useMemo } from "react";
import {
  generateTimePoints,
  getSegmentStatus,
  isRangeAvailable,
  toMinutes,
  type SlotStatus,
} from "@/lib/booking-conflicts";

interface TimeRange {
  start: string;
  end: string;
}

interface TimeGridProps {
  operatingStart: string;
  operatingEnd: string;
  blockedRanges: TimeRange[];
  selectedStart: string | null;
  selectedEnd: string | null;
  onSelect: (start: string | null, end: string | null) => void;
}

function chipClass(
  status: SlotStatus,
  inRange: boolean,
  isPending: boolean,
): string {
  if (inRange)
    return "bg-school-gold text-school-navy shadow-md ring-2 ring-school-gold-dark/30 border-school-gold font-bold";
  if (isPending)
    return "bg-school-gold text-school-navy shadow-md ring-2 ring-school-gold-dark/30 border-school-gold font-bold";
  if (status === "blocked")
    return "bg-school-navy/10 text-school-navy/40 border-school-navy/15 cursor-not-allowed";
  if (status === "grace")
    return "bg-school-navy/[0.06] text-school-navy/30 border-school-navy/10 cursor-not-allowed";
  return "bg-white text-gray-600 shadow-sm hover:shadow-md hover:bg-school-gold/10 hover:border-school-gold/40 active:scale-[0.97] cursor-pointer";
}

export function TimeGrid({
  operatingStart,
  operatingEnd,
  blockedRanges,
  selectedStart,
  selectedEnd,
  onSelect,
}: TimeGridProps) {
  const timePoints = useMemo(
    () => generateTimePoints(operatingStart, operatingEnd),
    [operatingStart, operatingEnd],
  );

  const segments = useMemo(
    () =>
      timePoints.slice(0, -1).map((pt, i) => ({
        start: pt,
        end: timePoints[i + 1],
        status: getSegmentStatus(pt, timePoints[i + 1], blockedRanges) as SlotStatus,
      })),
    [timePoints, blockedRanges],
  );

  const startM = selectedStart ? toMinutes(selectedStart) : null;
  const endM = selectedEnd ? toMinutes(selectedEnd) : null;

  function handleClick(segStart: string, status: SlotStatus) {
    if (status !== "available") return;
    const m = toMinutes(segStart);
    if (startM === null) { onSelect(segStart, null); return; }
    if (m === startM) { onSelect(null, null); return; }
    if (m < startM) { onSelect(segStart, null); return; }
    if (!isRangeAvailable(selectedStart!, segStart, blockedRanges)) {
      onSelect(segStart, null);
      return;
    }
    onSelect(selectedStart, segStart);
  }

  const fmt = (t: string) => {
    const [h, m] = t.split(":");
    return `${parseInt(h)}:${m}`;
  };

  return (
    <div className="select-none space-y-3">
      <p className="text-[13px] text-gray-500 leading-snug">
        {startM === null
          ? "Tap a time to set your start."
          : endM !== null
            ? "All set! Tap any slot to change."
            : "Now tap when you\u2019d like to finish."}
      </p>

      <div className="grid grid-cols-4 gap-1.5">
        {segments.map((seg) => {
          const sM = toMinutes(seg.start);
          const inRange = startM !== null && endM !== null && sM >= startM && sM < endM;
          const isPending = startM !== null && endM === null && sM === startM;

          return (
            <button
              key={seg.start}
              type="button"
              disabled={seg.status !== "available"}
              onClick={() => handleClick(seg.start, seg.status)}
              className={[
                "rounded-lg border border-gray-200/80 py-2 text-[13px] font-medium text-center transition-all duration-150",
                chipClass(seg.status, inRange, isPending),
              ].join(" ")}
            >
              {fmt(seg.start)}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4 text-[11px] text-gray-400">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-white shadow-sm ring-1 ring-gray-200" />
          Open
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-school-navy/20" />
          Taken
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-school-gold" />
          Yours
        </span>
      </div>

      {startM !== null && endM !== null && (
        <div className="flex items-center justify-between rounded-xl bg-school-gold/15 border border-school-gold/30 px-4 py-2.5">
          <span className="text-sm font-bold text-school-navy tracking-tight">
            {`${fmt(selectedStart!)} \u2013 ${fmt(selectedEnd!)}`}
          </span>
          <button
            type="button"
            onClick={() => onSelect(null, null)}
            className="text-xs font-medium text-gray-400 hover:text-red-500 transition-colors"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}
